// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: due dates for scratchpad lines. "/tomorrow", "@fri", "@next week", "@in 2h",
// "@17 sep" and friends resolve to a Date; a chip shows it and a reminder fires then.
// Markdown form: "@due(2026-09-17 09:00)", so the pad stays plain text.

export const DUE_PATTERN = /@due\((\d{4}-\d{2}-\d{2})(?: (\d{2}:\d{2}))?\)/;
const DEFAULT_HOUR = 9;
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function at(date: Date, hour = DEFAULT_HOUR, minute = 0): Date {
	const next = new Date(date);
	next.setHours(hour, minute, 0, 0);
	return next;
}

function addDays(date: Date, days: number): Date {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
}

function nextWeekday(from: Date, weekday: number, allowToday = false): Date {
	let delta = (weekday - from.getDay() + 7) % 7;
	if (delta === 0 && !allowToday) delta = 7;
	return addDays(from, delta);
}

/** Parses a phrase into a due date, or null when it is not one. `now` is injectable for tests. */
export function parseDuePhrase(raw: string, now = new Date()): Date | null {
	const text = raw
		.trim()
		.toLowerCase()
		.replace(/^(due|by|on)\s+/, '');
	if (!text) return null;
	const time = /(?:\s+at)?\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/.exec(text);
	let hour = DEFAULT_HOUR;
	let minute = 0;
	let phrase = text;
	// A bare trailing number is only a time after "at" ("fri at 3"); "sep 17" is a day.
	if (time && (time[2] || time[3] || /\sat\s+\d/.test(text))) {
		hour = Number(time[1]);
		minute = Number(time[2] ?? 0);
		if (time[3] === 'pm' && hour < 12) hour += 12;
		if (time[3] === 'am' && hour === 12) hour = 0;
		phrase = text.slice(0, time.index).trim() || 'today';
	}
	if (phrase === 'today' || phrase === 'tod') return at(now, hour, minute);
	if (phrase === 'tomorrow' || phrase === 'tmr' || phrase === 'tom') return at(addDays(now, 1), hour, minute);
	if (phrase === 'tonight') return at(now, 19, 0);
	if (phrase === 'next week' || phrase === 'nw') return at(nextWeekday(addDays(now, 1), 1), hour, minute);
	if (phrase === 'next month') {
		const next = new Date(now);
		next.setMonth(next.getMonth() + 1, 1);
		return at(next, hour, minute);
	}
	if (phrase === 'weekend' || phrase === 'this weekend') return at(nextWeekday(now, 6, true), hour, minute);
	const relative = /^in\s+(\d+)\s*(m|min|mins|minutes?|h|hr|hrs|hours?|d|days?|w|wk|weeks?)$/.exec(phrase);
	if (relative) {
		const amount = Number(relative[1]);
		const unit = relative[2][0];
		const next = new Date(now);
		if (unit === 'm') next.setMinutes(next.getMinutes() + amount);
		else if (unit === 'h') next.setHours(next.getHours() + amount);
		else if (unit === 'd') return at(addDays(now, amount), hour, minute);
		else if (unit === 'w') return at(addDays(now, amount * 7), hour, minute);
		next.setSeconds(0, 0);
		return next;
	}
	const weekday = /^(?:next\s+)?(sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)[a-z]*$/.exec(phrase);
	if (weekday) {
		const index = DAYS.findIndex((day) => day.startsWith(weekday[1].slice(0, 3)));
		const base = phrase.startsWith('next') ? addDays(nextWeekday(now, index), 0) : nextWeekday(now, index);
		return at(base, hour, minute);
	}
	const dayMonth = /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]{3})[a-z]*(?:\s+(\d{4}))?$/.exec(phrase) ?? monthDay(phrase);
	if (dayMonth) {
		const month = MONTHS.indexOf(dayMonth[2]);
		if (month >= 0) {
			const year = dayMonth[3] ? Number(dayMonth[3]) : now.getFullYear();
			const date = at(new Date(year, month, Number(dayMonth[1])), hour, minute);
			if (!dayMonth[3] && date.getTime() < now.getTime() - 86_400_000) date.setFullYear(year + 1);
			return date;
		}
	}
	const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(phrase);
	if (iso) return at(new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])), hour, minute);
	const slash = /^(\d{1,2})[/.](\d{1,2})(?:[/.](\d{2,4}))?$/.exec(phrase);
	if (slash) {
		const year = slash[3] ? Number(slash[3].length === 2 ? `20${slash[3]}` : slash[3]) : now.getFullYear();
		return at(new Date(year, Number(slash[2]) - 1, Number(slash[1])), hour, minute);
	}
	return null;
}

function monthDay(phrase: string): RegExpExecArray | null {
	const match = /^([a-z]{3})[a-z]*\s+(\d{1,2})(?:st|nd|rd|th)?(?:\s+(\d{4}))?$/.exec(phrase);
	if (!match) return null;
	return [match[0], match[2], match[1], match[3]] as unknown as RegExpExecArray;
}

const pad = (value: number) => String(value).padStart(2, '0');

export function toDueMarkdown(date: Date): string {
	const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
	const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
	return `@due(${day}${time === `${pad(DEFAULT_HOUR)}:00` ? '' : ` ${time}`})`;
}

export function fromDueMarkdown(day: string, time?: string | null): Date {
	const [year, month, date] = day.split('-').map(Number);
	const [hour, minute] = (time ?? `${pad(DEFAULT_HOUR)}:00`).split(':').map(Number);
	return new Date(year, month - 1, date, hour, minute, 0, 0);
}

/** "Today 14:00", "Tomorrow", "Fri 26", "Mon 5 Oct", "3 Oct 2027". */
export function formatDue(date: Date, now = new Date()): string {
	const startOfToday = at(now, 0, 0).getTime();
	const dayIndex = Math.floor((at(date, 0, 0).getTime() - startOfToday) / 86_400_000);
	const showTime = !(date.getHours() === DEFAULT_HOUR && date.getMinutes() === 0);
	const time = showTime ? ` ${pad(date.getHours())}:${pad(date.getMinutes())}` : '';
	if (dayIndex === 0) return `Today${time}`;
	if (dayIndex === 1) return `Tomorrow${time}`;
	if (dayIndex === -1) return `Yesterday${time}`;
	const weekday = date.toLocaleDateString(undefined, {weekday: 'short'});
	const dayMonth = date.toLocaleDateString(undefined, {day: 'numeric', month: 'short'});
	if (dayIndex > 1 && dayIndex < 7) return `${weekday} ${date.getDate()}${time}`;
	if (date.getFullYear() === now.getFullYear()) return `${weekday} ${dayMonth}${time}`;
	return `${dayMonth} ${date.getFullYear()}${time}`;
}

export function dueState(date: Date, now = new Date()): 'overdue' | 'today' | 'soon' | 'later' {
	if (date.getTime() < now.getTime()) return 'overdue';
	const dayIndex = Math.floor((at(date, 0, 0).getTime() - at(now, 0, 0).getTime()) / 86_400_000);
	if (dayIndex === 0) return 'today';
	if (dayIndex <= 2) return 'soon';
	return 'later';
}

export interface DueSuggestion {
	label: string;
	date: Date;
}

/** Suggestions for a typed query: exact phrases first, then the usual shortcuts. */
export function suggestDues(query: string, now = new Date()): Array<DueSuggestion> {
	const q = query.trim();
	const out: Array<DueSuggestion> = [];
	const push = (label: string, date: Date | null) => {
		if (date && !out.some((entry) => entry.date.getTime() === date.getTime())) out.push({label, date});
	};
	if (q) push(q, parseDuePhrase(q, now));
	const defaults: Array<[string, string]> = [
		['Today', 'today'],
		['Tomorrow', 'tomorrow'],
		['Friday', 'friday'],
		['Next week', 'next week'],
		['In 2 hours', 'in 2h'],
	];
	for (const [label, phrase] of defaults) {
		if (!q || label.toLowerCase().includes(q.toLowerCase())) push(label, parseDuePhrase(phrase, now));
	}
	return out.slice(0, 5);
}
